import { useTranslation } from 'react-i18next'
import { HugeiconsIcon } from '@hugeicons/react'
import {
    AlertCircleStrokeRounded,
    FileUploadStrokeRounded,
    SquareLock02StrokeRounded,
} from '@hugeicons-pro/core-stroke-rounded'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import { ATTACHMENT_MAX_PER_PARENT } from '@/common/utils/Constants/Constants'

import { UPLOAD_STATE } from './useAttachmentUploads'

// One segment per file in the batch. A file that left the queue has landed (the list shows how).
const segmentColor = (item) => {
    if (!item) return 'success.main'
    if (item.state === UPLOAD_STATE.done) return item.accepted ? 'success.main' : 'text.disabled'
    if (item.state === UPLOAD_STATE.error) return 'error.main'
    if (item.state === UPLOAD_STATE.checking) return 'secondary.main'
    if (item.state === UPLOAD_STATE.waiting) return 'action.disabledBackground'
    return 'primary.light'
}

// The zone keeps one height in every state, the uploading one's (progress line plus segments),
// so the table under it never shifts when the state changes. Only held files and a running
// batch together, which show two lines, grow past it.
const ZONE_MIN_HEIGHT = 62

const linkButton = {
    fontSize: 'inherit',
    fontWeight: 600,
    minWidth: 0,
    p: 0,
    textDecoration: 'underline',
    textTransform: 'none',
    textUnderlineOffset: 3,
    verticalAlign: 'baseline',
    '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
}

/**
 * UploadZone - The one block between the documents header and the table. It does one job at a
 * time: take a drop or a pick, warn about a selection over the cap, report the batch being sent,
 * or say why nothing more fits. Held files and a running batch can coexist (a retry while files
 * are held), so those two lines stack inside the zone. The whole card accepts drops; this zone
 * is where that shows.
 *
 * @param {Object} props
 * @param {Array} props.batchKeys - Keys of the files in the batch being sent
 * @param {number} props.excess - Held files beyond the free slots
 * @param {number} props.freeSlots
 * @param {number} props.heldCount - Files waiting for the user to send them
 * @param {boolean} props.inFlight
 * @param {boolean} props.isDragActive - Files are being dragged over the card
 * @param {Array} props.items - Queue entries from useAttachmentUploads
 * @param {Function} props.onAdd - Opens the file picker
 * @param {Function} props.onSendHeld - () => void
 */
const UploadZone = ({ batchKeys, excess, freeSlots, heldCount, inFlight, isDragActive, items, onAdd, onSendHeld }) => {
    const { t } = useTranslation()

    const byKey = new Map(items.map((item) => [item.key, item]))
    const batch = batchKeys.map((key) => byKey.get(key))
    const finished = batch.filter(
        (item) => !item || item.state === UPLOAD_STATE.error || item.state === UPLOAD_STATE.done
    ).length
    const checking = batch.find((item) => item?.state === UPLOAD_STATE.checking)
    const isFull = freeSlots === 0
    const isIdle = !heldCount && !inFlight

    const heldLine = heldCount ? (
        <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-between' }}>
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.25 }}>
                {excess ? (
                    <Box
                        component="span"
                        sx={{ color: 'primary.dark', display: 'inline-flex' }}
                    >
                        <HugeiconsIcon
                            icon={AlertCircleStrokeRounded}
                            size={20}
                        />
                    </Box>
                ) : null}
                <Typography variant="body2">
                    {excess
                        ? t('Only {{free}} of the {{selected}} selected files fit. Remove {{excess}} to continue.', {
                              free: freeSlots,
                              selected: heldCount,
                              excess,
                          })
                        : t('Ready to upload.')}
                </Typography>
            </Box>
            <Button
                aria-label={t('Upload selected files')}
                disabled={inFlight || excess > 0}
                size="small"
                variant="contained"
                onClick={onSendHeld}
            >
                {heldCount === 1 ? t('Upload 1 file') : t('Upload {{count}} files', { count: heldCount })}
            </Button>
        </Box>
    ) : null

    const progressLine = inFlight ? (
        <>
            <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <CircularProgress
                    color="secondary"
                    size={18}
                    thickness={5}
                    aria-hidden
                    disableShrink
                />
                <Typography
                    fontWeight={600}
                    variant="body2"
                >
                    {t('Uploading {{current}} of {{total}}', {
                        current: Math.min(finished + 1, batch.length),
                        total: batch.length,
                    })}
                </Typography>
                {checking ? (
                    <Typography
                        color="text.secondary"
                        variant="body2"
                    >
                        {t('· checking {{name}}', { name: checking.file.name })}
                    </Typography>
                ) : null}
                <Typography
                    color="text.secondary"
                    variant="body2"
                >
                    {t('· you can add more when this batch is done')}
                </Typography>
            </Box>
            <Box
                sx={{ display: 'flex', gap: 0.5 }}
                aria-hidden
            >
                {batchKeys.map((key, index) => (
                    <Box
                        key={key}
                        sx={{ bgcolor: segmentColor(batch[index]), borderRadius: 1, flex: 1, height: 6 }}
                    />
                ))}
            </Box>
        </>
    ) : null

    const restLine = isFull ? (
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, justifyContent: 'center' }}>
            <HugeiconsIcon
                icon={SquareLock02StrokeRounded}
                size={20}
            />
            <Typography variant="body2">
                {t(
                    'All {{max}} staff slots are used. Remove a staff attachment, including any not accepted, to add another.',
                    {
                        max: ATTACHMENT_MAX_PER_PARENT,
                    }
                )}
            </Typography>
        </Box>
    ) : (
        <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
            <HugeiconsIcon
                icon={FileUploadStrokeRounded}
                size={20}
            />
            <Typography variant="body2">
                {t('Drop files anywhere on this card, or')}{' '}
                <Button
                    size="small"
                    sx={linkButton}
                    onClick={onAdd}
                >
                    {t('add attachments')}
                </Button>
                {t('. New files start as staff only.')}
            </Typography>
        </Box>
    )

    const tone = excess ? 'warn' : isIdle && !isFull ? 'drop' : 'plain'

    return (
        <Box
            sx={{
                backgroundColor:
                    tone === 'warn'
                        ? 'rgba(247, 143, 30, 0.12)'
                        : isDragActive
                          ? 'rgba(247, 143, 30, 0.06)'
                          : isIdle && isFull
                            ? 'action.hover'
                            : 'transparent',
                border: (theme) =>
                    tone === 'warn'
                        ? 'none'
                        : tone === 'drop'
                          ? `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.action.disabled}`
                          : `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                boxSizing: 'border-box',
                color: isDragActive ? 'primary.main' : 'text.secondary',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
                justifyContent: 'center',
                minHeight: ZONE_MIN_HEIGHT,
                mx: 1.5,
                px: 2,
                py: 1.5,
            }}
        >
            {heldLine}
            {progressLine}
            {isIdle ? restLine : null}
        </Box>
    )
}

UploadZone.propTypes = {
    batchKeys: PropTypes.array.isRequired,
    excess: PropTypes.number.isRequired,
    freeSlots: PropTypes.number.isRequired,
    heldCount: PropTypes.number.isRequired,
    inFlight: PropTypes.bool.isRequired,
    isDragActive: PropTypes.bool.isRequired,
    items: PropTypes.array.isRequired,
    onAdd: PropTypes.func.isRequired,
    onSendHeld: PropTypes.func.isRequired,
}

export default UploadZone
